import { formatPhone } from "@elektr/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { Button } from "@/components/ui/button";
import { Card, PageHeader } from "@/components/ui/card";
import { Field, Input } from "@/components/ui/form";
import { useToast } from "@/components/ui/toast";
import { ApiError, api, errorMessage, unwrap } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";

/** Har kim o'z ismi/emailini o'zgartiradi va parolni o'zi qo'yadi (admin/super admin ham). */
export function ProfilePage() {
  const { t } = useT();
  const { user, login } = useAuth();
  if (!user) return null;
  return (
    <>
      <PageHeader title={t("profile.title")} />
      <div className="grid max-w-xl gap-4">
        <InfoCard key={user.id} />
        <PasswordCard phone={user.phone} login={login} />
        <p className="text-sm text-muted-foreground">
          {t("profile.login_note", { phone: formatPhone(user.phone) })}
        </p>
      </div>
    </>
  );
}

function InfoCard() {
  const { t } = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [name, setName] = useState(user?.full_name ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: async () =>
      unwrap(await api.PATCH("/api/auth/me/", { body: { full_name: name, email: email.trim() } })),
    onSuccess: () => {
      setError(null);
      toast.success(t("common.saved"));
      void qc.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (e) => setError(errorMessage(e, t("common.error"))),
  });
  return (
    <Card>
      <h2 className="mb-3 font-bold">{t("profile.info")}</h2>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <Field label={t("users.full_name")}>
          {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <Field label={t("users.email")} hint={t("profile.email_hint")}>
          {(id) => (
            <Input
              id={id}
              type="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </Field>
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        <Button type="submit" disabled={save.isPending} className="self-start">
          {t("common.save")}
        </Button>
      </form>
    </Card>
  );
}

function PasswordCard({
  phone,
  login,
}: {
  phone: string;
  login: (phone: string, password: string) => Promise<unknown>;
}) {
  const { t } = useT();
  const toast = useToast();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const [error, setError] = useState<string | null>(null);

  const change = useMutation({
    mutationFn: async () => {
      const res = await api.POST("/api/auth/password/change/", {
        body: { current_password: current, new_password: next },
      });
      if (res.response.status === 400) {
        const b = res.error as { code?: string; new_password?: string[] } | undefined;
        throw new Error(
          b?.code === "wrong_password"
            ? t("profile.wrong_password")
            : (b?.new_password?.join(" ") ?? t("common.error")),
        );
      }
      if (!res.response.ok) throw new ApiError(res.response.status, res.error);
      // Eski sessiyalar tugatildi: yangi parol bilan o'zimiz qayta kiramiz (foydalanuvchi chiqib ketmaydi)
      await login(phone, next);
    },
    onSuccess: () => {
      setCurrent("");
      setNext("");
      setRepeat("");
      setError(null);
      toast.success(t("profile.password_changed"));
    },
    onError: (e) => setError(e instanceof Error ? e.message : t("common.error")),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (next.length < 8) return setError(t("users.password_short"));
    if (next !== repeat) return setError(t("auth.password_mismatch"));
    change.mutate();
  };

  return (
    <Card>
      <h2 className="mb-3 font-bold">{t("profile.password")}</h2>
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Field label={t("profile.current_password")}>
          {(id) => (
            <Input
              id={id}
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          )}
        </Field>
        <Field label={t("auth.new_password")} hint={t("users.password_hint_short")}>
          {(id) => (
            <Input
              id={id}
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
          )}
        </Field>
        <Field label={t("auth.repeat_password")}>
          {(id) => (
            <Input
              id={id}
              type="password"
              autoComplete="new-password"
              value={repeat}
              onChange={(e) => setRepeat(e.target.value)}
            />
          )}
        </Field>
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        <Button type="submit" disabled={change.isPending || !current} className="self-start">
          {t("profile.change_password")}
        </Button>
      </form>
    </Card>
  );
}
